import * as log from '../util/log';
import { storableError } from '../util/errors';
import { clearCurrentUser, fetchCurrentUser, fetchUserCategoriesWithListings } from './user.duck';
import { createUserWithIdp, validateIdentityUniqueness } from '../util/api';
import { trackAccountRegistered } from '../util/gtm';
import {
  getActiveCountryCode,
  getIdentityFieldForUserType,
  getLegacyIdentityFieldForUserType,
  normalizeIdentityValue,
  normalizeOfficialStoreIdentityValue,
} from '../util/identity';

const validateIdentityForSignup = params => {
  const userType = params?.publicData?.userType;
  const privateData = params?.privateData || {};
  const countryCode = getActiveCountryCode();

  if (userType === 'cliente') {
    const customerIdentityField = getIdentityFieldForUserType('cliente', countryCode);
    const customerLegacyField = getLegacyIdentityFieldForUserType('cliente');
    const customerIdentity = normalizeIdentityValue(
      privateData[customerIdentityField] || (customerLegacyField && privateData[customerLegacyField])
    );

    if (customerIdentity) {
      return validateIdentityUniqueness({
        userType,
        [customerIdentityField]: customerIdentity,
        ...(customerLegacyField && customerLegacyField !== customerIdentityField
          ? { [customerLegacyField]: customerIdentity }
          : {}),
      });
    }
  }

  if (userType === 'tienda-oficial') {
    const storeIdentityField = getIdentityFieldForUserType('tienda-oficial', countryCode);
    const storeLegacyField = getLegacyIdentityFieldForUserType('tienda-oficial');
    const storeIdentity = normalizeOfficialStoreIdentityValue(
      privateData[storeIdentityField] || (storeLegacyField && privateData[storeLegacyField]),
      countryCode
    );

    if (storeIdentity) {
      return validateIdentityUniqueness({
        userType,
        [storeIdentityField]: storeIdentity,
        ...(storeLegacyField && storeLegacyField !== storeIdentityField
          ? { [storeLegacyField]: storeIdentity }
          : {}),
      });
    }
  }

  return Promise.resolve();
};

const authenticated = authInfo => authInfo?.isAnonymous === false;
const loggedInAs = authInfo => authInfo?.isLoggedInAs === true;

// ================ Action types ================ //

export const AUTH_INFO_REQUEST = 'app/auth/AUTH_INFO_REQUEST';
export const AUTH_INFO_SUCCESS = 'app/auth/AUTH_INFO_SUCCESS';

export const LOGIN_REQUEST = 'app/auth/LOGIN_REQUEST';
export const LOGIN_SUCCESS = 'app/auth/LOGIN_SUCCESS';
export const LOGIN_ERROR = 'app/auth/LOGIN_ERROR';

export const LOGOUT_REQUEST = 'app/auth/LOGOUT_REQUEST';
export const LOGOUT_SUCCESS = 'app/auth/LOGOUT_SUCCESS';
export const LOGOUT_ERROR = 'app/auth/LOGOUT_ERROR';

export const SIGNUP_REQUEST = 'app/auth/SIGNUP_REQUEST';
export const SIGNUP_SUCCESS = 'app/auth/SIGNUP_SUCCESS';
export const SIGNUP_ERROR = 'app/auth/SIGNUP_ERROR';

export const CONFIRM_REQUEST = 'app/auth/CONFIRM_REQUEST';
export const CONFIRM_SUCCESS = 'app/auth/CONFIRM_SUCCESS';
export const CONFIRM_ERROR = 'app/auth/CONFIRM_ERROR';

// Generic user_logout action that can be handled elsewhere
// E.g. src/reducers.js clears store as a consequence
export const USER_LOGOUT = 'app/USER_LOGOUT';

// ================ Reducer ================ //

const initialState = {
  isAuthenticated: false,

  // is marketplace operator logged in as a marketplace user
  isLoggedInAs: false,

  // scopes associated with current token
  authScopes: [],

  // auth info
  authInfoLoaded: false,

  // login
  loginError: null,
  loginInProgress: false,

  // logout
  logoutError: null,
  logoutInProgress: false,

  // signup
  signupError: null,
  signupInProgress: false,

  // confirm (create use with idp)
  confirmError: null,
  confirmInProgress: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case AUTH_INFO_REQUEST:
      return state;
    case AUTH_INFO_SUCCESS:
      return {
        ...state,
        authInfoLoaded: true,
        isAuthenticated: authenticated(payload),
        isLoggedInAs: loggedInAs(payload),
        authScopes: payload.scopes,
      };

    case LOGIN_REQUEST:
      return {
        ...state,
        loginInProgress: true,
        loginError: null,
        logoutError: null,
        signupError: null,
      };
    case LOGIN_SUCCESS:
      return { ...state, loginInProgress: false, isAuthenticated: true };
    case LOGIN_ERROR:
      return { ...state, loginInProgress: false, loginError: payload };

    case LOGOUT_REQUEST:
      return { ...state, logoutInProgress: true, loginError: null, logoutError: null };
    case LOGOUT_SUCCESS:
      return {
        ...state,
        logoutInProgress: false,
        isAuthenticated: false,
        isLoggedInAs: false,
        authScopes: [],
      };
    case LOGOUT_ERROR:
      return { ...state, logoutInProgress: false, logoutError: payload };

    case SIGNUP_REQUEST:
      return { ...state, signupInProgress: true, loginError: null, signupError: null };
    case SIGNUP_SUCCESS:
      return { ...state, signupInProgress: false };
    case SIGNUP_ERROR:
      return { ...state, signupInProgress: false, signupError: payload };

    case CONFIRM_REQUEST:
      return { ...state, confirmInProgress: true, loginError: null, confirmError: null };
    case CONFIRM_SUCCESS:
      return { ...state, confirmInProgress: false, isAuthenticated: true };
    case CONFIRM_ERROR:
      return { ...state, confirmInProgress: false, confirmError: payload };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const authenticationInProgress = state => {
  const { loginInProgress, logoutInProgress, signupInProgress, confirmInProgress } = state.auth;
  return loginInProgress || logoutInProgress || signupInProgress || confirmInProgress;
};

// ================ Action creators ================ //

export const authInfoRequest = () => ({ type: AUTH_INFO_REQUEST });
export const authInfoSuccess = info => ({ type: AUTH_INFO_SUCCESS, payload: info });

export const loginRequest = () => ({ type: LOGIN_REQUEST });
export const loginSuccess = () => ({ type: LOGIN_SUCCESS });
export const loginError = error => ({ type: LOGIN_ERROR, payload: error, error: true });

export const logoutRequest = () => ({ type: LOGOUT_REQUEST });
export const logoutSuccess = () => ({ type: LOGOUT_SUCCESS });
export const logoutError = error => ({ type: LOGOUT_ERROR, payload: error, error: true });

export const signupRequest = () => ({ type: SIGNUP_REQUEST });
export const signupSuccess = () => ({ type: SIGNUP_SUCCESS });
export const signupError = error => ({ type: SIGNUP_ERROR, payload: error, error: true });

export const confirmRequest = () => ({ type: CONFIRM_REQUEST });
export const confirmSuccess = () => ({ type: CONFIRM_SUCCESS });
export const confirmError = error => ({ type: CONFIRM_ERROR, payload: error, error: true });

export const userLogout = () => ({ type: USER_LOGOUT });

// ================ Thunks ================ //

export const authInfo = () => (dispatch, getState, sdk) => {
  dispatch(authInfoRequest());
  return sdk
    .authInfo()
    .then(info => dispatch(authInfoSuccess(info)))
    .catch(e => {
      // Requesting auth info just reads the token from the token
      // store (i.e. cookies), and should not fail in normal
      // circumstances. If it fails, it's due to a programming
      // error. In that case we mark the operation done and dispatch
      // `null` success action that marks the user as unauthenticated.
      log.error(e, 'auth-info-failed');
      dispatch(authInfoSuccess(null));
    });
};

export const login = (username, password) => (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(loginRequest());

  // Note that the thunk does not reject when the login fails, it
  // just dispatches the login error action.
  return sdk
    .login({ username, password })
    .then(() => dispatch(fetchCurrentUser({ afterLogin: true })))
    .then(() => dispatch(fetchUserCategoriesWithListings()))
    .then(() => dispatch(loginSuccess()))
    .catch(e => dispatch(loginError(storableError(e))));
};

export const logout = () => (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(logoutRequest());

  // Note that the thunk does not reject when the logout fails, it
  // just dispatches the logout error action.
  return sdk
    .logout()
    .then(() => {
      // The order of the dispatched actions
      dispatch(logoutSuccess());
      dispatch(clearCurrentUser());
      log.clearUserId();
      dispatch(userLogout());
    })
    .catch(e => dispatch(logoutError(storableError(e))));
};

export const signup = params => (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(signupRequest());
  // Note: params are already structured on AuthenticationPage (handleSubmitSignup)
  if(params.publicData.userType === 'tienda-oficial'){
    params.publicData.isPayedAccount = false;
  }
  // We must login the user if signup succeeds since the API doesn't
  // do that automatically.
  return validateIdentityForSignup(params)
    .then(() => sdk.currentUser.create(params))
    .then((res) => {
      //console.log("🩷🩷🩷🩷🩷", res)
      if(params.publicData.userType === 'tienda-oficial'){
        // crear el listing
        dispatch(createUserProfileIfNeeded(res.data.data.id.uuid));
      }
      dispatch(signupSuccess());
    })
    .then(() => {
      //alert("signup")
      dispatch(signupSuccess());

      trackAccountRegistered({
        user_type: params.publicData?.userType || 'customer',
        registration_method: 'email',
        //first_name: params.firstName,
        //last_name: params.lastName,
      });

    })
    .then(() => dispatch(login(params.email, params.password)))
    .catch(e => {
      dispatch(signupError(storableError(e)));
      log.error(e, 'signup-failed', {
        email: params.email,
        firstName: params.firstName,
        lastName: params.lastName,
      });
    });
};

export const signupWithIdp = params => (dispatch, getState, sdk) => {
  dispatch(confirmRequest());
  return validateIdentityForSignup(params)
    .then(() => createUserWithIdp(params))
    .then(res => {
      //return dispatch(confirmSuccess());
      dispatch(confirmSuccess());
      
      // Disparar evento GTM para registro con IdP
      trackAccountRegistered({
        user_type: params.publicData?.userType || 'customer',
        registration_method: 'social', // o puedes ser más específico
        //first_name: params.firstName,
        //last_name: params.lastName,
      });
      
      return res;
    })
    .then(() => dispatch(fetchCurrentUser()))
    .catch(e => {
      log.error(e, 'create-user-with-idp-failed', { params });
      return dispatch(confirmError(storableError(e)));
    });
};

// Nuevo thunk para crear perfil de usuario
export const createUserProfileIfNeeded = (uuid) => (dispatch, getState, sdk) => {
  //console.log("🚀🚀🚀🚀🚀 Crear perfil de usuario para:", uuid);
  
  /*

  const createEditUsername = `${process.env.REACT_APP_API_ROOT_URL.replace(/\/$/, '')}/api/create-or-update-user-profile?slug=${encodeURIComponent(uuid)}&uid=${encodeURIComponent(uuid)}`;
  console.log("createEditUsername", createEditUsername)
    // Aquí deberías hacer la consulta a tu API
    const response = fetch(createEditUsername);
  */
/*
    const response = fetch( `${process.env.REACT_APP_API_ROOT_URL.replace(/\/$/, '')}/api/create-or-update-user-profile?slug=${encodeURIComponent(uuid)}&uid=${encodeURIComponent(uuid)}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: 'include',
        body: JSON.stringify({ slug: encodeURIComponent(uuid), uid: encodeURIComponent(uuid) }),
      }
    );
*/

/*
    const response = fetch( `${process.env.REACT_APP_API_ROOT_URL.replace(/\/$/, '')}/api/create-or-update-user-profile`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: 'include',
            body: JSON.stringify({ slug: encodeURIComponent(uuid), uid: encodeURIComponent(uuid) }),
          }
        );
*/

  /*
  return sdk.ownListings.create({
    title: `Perfil de ${uuid}`,
    state: 'published',
    publicData: {
      listingType: 'user-profile'
      //isPayedAccount: false
    },
  })
  .then(response => {
    console.log('✅✅✅✅✅ Listing user-profile creado:', response.data.data);
    return response.data.data;
  });
  */
};
